import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { UploadOutlined } from "@mui/icons-material";
import { useAppProvider } from "../../Providers/appProvider/AppProvider";
import { Upload } from "antd";
import ImgCrop from "antd-img-crop";
import { NavBar } from "../NavBar";
import {
  userServiceAddFileToBucket,
  userServiceDeleteFileInBucket,
  userServiceSearchFilesInBucket,
} from "../../api";
import { accessKey, secretKey } from "../../config";
import { GlobalActionTypes } from "../../Providers/appProvider/reducers/globalReducer";

export const AccountProfile = (props) => {
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo")) || {};

  const maxSize = 1024 * 1024; // 1 MB in bytes
  const [userId, setUserId] = useState();
  const { state, dispatch } = useAppProvider();
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "",
    msg: "",
  });

  const {
    globalReducer: { profilePics, logoImage },
  } = state;

  const userRole = userInfo?.user_role;

  useEffect(() => {
    try {
      const userDetails = JSON.parse(sessionStorage.getItem("userInfo"));
      setUserId(userDetails.userid);
    } catch (error) {
      console.log(`Unable to set userId: ${error}`);
    }
  }, []);

  const changeProfileImage = async (imageFile) => {
    if (!userInfo?.userid) return;

    const bucketName = "user_service_media_bucket";
    const fileName = `${userInfo?.userid}_pic`;

    try {
      const doesImageExitInBrs =
        (await userServiceSearchFilesInBucket(bucketName, fileName)).key_list
          .length > 0;

      if (doesImageExitInBrs) {
        await userServiceDeleteFileInBucket(bucketName, fileName);
      }

      await userServiceAddFileToBucket(
        accessKey,
        secretKey,
        bucketName,
        fileName,
        imageFile,
        {
          onUploadProgress: () => { },
        }
      );

      const blob = new Blob([imageFile], {
        type: "image/png",
      });

      const imageURL = URL.createObjectURL(blob);

      dispatch({
        type: GlobalActionTypes.SET_PROFILE_PICS,
        payload: { ...profilePics, userid: userInfo?.userid, img: imageURL },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const changeLogoImage = async (imageFile) => {
    if (!userInfo?.userid) return;

    const bucketName = "user_service_media_bucket";
    const fileName = `${userInfo?.userid}_logo`;

    try {
      const doesImageExitInBrs =
        (await userServiceSearchFilesInBucket(bucketName, fileName)).key_list
          .length > 0;

      if (doesImageExitInBrs) {
        await userServiceDeleteFileInBucket(bucketName, fileName);
      }

      await userServiceAddFileToBucket(
        accessKey,
        secretKey,
        bucketName,
        fileName,
        imageFile,
        {
          onUploadProgress: () => { },
        }
      );

      const blob = new Blob([imageFile], {
        type: "image/png",
      });

      const imageURL = URL.createObjectURL(blob);

      dispatch({
        type: GlobalActionTypes.SET_LOGO_IMAGE,
        payload: { img: imageURL },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const profilePicUpload = (imageFile) => {
    try {
      if (imageFile.size > maxSize) {
        setSnackbar({
          open: true,
          severity: "error",
          msg: "File size cannot be more than 1 MB",
        });
        return { success: false };
      } else {
        changeProfileImage(imageFile);
        return { success: true };
      }
    } catch (error) { }
  };

  const logoPicUpload = (imageFile) => {
    try {
      if (imageFile.size > maxSize) {
        setSnackbar({
          open: true,
          severity: "error",
          msg: "File size cannot be more than 1 MB",
        });
        return { success: false };
      } else {
        changeLogoImage(imageFile);
        return { success: true };
      }
    } catch (error) { }
  };

  const first_name = userInfo?.first_name;
  const last_name = userInfo?.last_name;
  const userstate = userInfo?.state;
  const country = userInfo?.country;
  const accountId = userInfo?.user_account;
  // const { first_name, last_name, state, country } = userInfo;

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, severity: "", msg: "" })}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, severity: "", msg: "" })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

      <Grid item lg={6} md={12} xs={12}>
        <Card
          sx={{
            height: "100%",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                mb: 2,
                alignItems: "center",
              }}
            >
              <CustomUploader
                imageData={profilePics?.[userInfo?.userid]}
                uploadFunction={profilePicUpload}
                aspect={1 / 1}
                type="PROFILE"
                isOwner={userRole === "OWNER"}
              />
              <Box sx={{ textAlign: "center" }}>
                <Typography gutterBottom variant="h5" component="div">
                  {`${first_name || ""} ${last_name || ""}`}
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  {`Account ID : ${accountId}`}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  mt: 2,
                  justifyContent: "space-evenly",
                }}
              >
                {userstate && (
                  <Typography mx={2} color="textSecondary" variant="body2">
                    {`State : ${userstate || ''}`}
                  </Typography>
                )}
                {country && (
                  <Typography mx={2} color="textSecondary" variant="body2">
                    {`Country : ${country || ''}`}
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
                pt: 2,
              }}
            >
              <CustomUploader
                imageData={logoImage}
                uploadFunction={logoPicUpload}
                aspect={16 / 9}
                type="LOGO"
                isOwner={userRole === "OWNER"}
              />
              <Box>
                <Typography mt={2} color="textSecondary" variant="body2">
                  Company Profile
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};

const CustomUploader = ({
  imageData,
  uploadFunction,
  aspect,
  type,
  isOwner,
}) => {
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    const getURL = async (imageData) => {
      try {
        const base64String = imageData?.split(",")[1];
        if (!base64String) return;
        const binaryString = atob(base64String);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          byteArray[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([byteArray], {
          type: "application/octet-stream",
        });

        const fileURL = URL.createObjectURL(blob);
        setFileList([
          {
            uid: "-1",
            name: "Profile",
            status: "done",
            url: fileURL,
          },
        ]);
      } catch (err) {
        console.log(err);
      }
    };
    if (imageData) {
      getURL(imageData);
    }
  }, [imageData]);

  const onPreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow.document.write(image.outerHTML);
  };

  return (
    <ImgCrop aspect={aspect} showGrid={true} rotate>
      <Upload
        style={{ height: 52, width: 52 }}
        showUploadList={false}
        disabled={!isOwner && type === "LOGO"}
        beforeUpload={(file) => {
          const res = uploadFunction(file);
          if (res.success) {
            setFileList([file]);
          } else {
            return false;
          }
          setFileList([file]);
          return false;
        }}
        fileList={fileList}
        onRemove={() => {
          setFileList([]);
        }}
        onPreview={onPreview}
      >
        {imageData ? (
          <>
            {type === "PROFILE" ? (
              <Tooltip title="Change Profile Pic">
                <div className="relative group">
                  <Avatar
                    src={imageData}
                    className="cursor-pointer"
                    sx={{
                      height: 52,
                      width: 52,
                    }}
                  />
                  <div className="absolute rounded-full cursor-pointer inset-0 bg-black opacity-0 transition-opacity duration-300 group-hover:opacity-50"></div>
                  <div className="absolute inset-0 flex opacity-0 group-hover:opacity-100 cursor-pointer items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4 text-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z"
                      />
                    </svg>
                  </div>
                </div>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  isOwner
                    ? "Change Company Logo"
                    : "Only Owner can change company logo"
                }
              >
                <div className="relative group bg-slate-200 p-0.5">
                  <img src={imageData} className="cursor-pointer h-14 w-28" />
                  <div className="absolute cursor-pointer inset-0 bg-black opacity-0 transition-opacity duration-300 group-hover:opacity-50"></div>
                  <div className="absolute inset-0 flex opacity-0 group-hover:opacity-100 cursor-pointer items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4 text-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z"
                      />
                    </svg>
                  </div>
                </div>
              </Tooltip>
            )}
          </>
        ) : (
          <Tooltip title={!isOwner && type === "LOGO" ? 'Only Owners can change Logo' : 'Change Image'}>
            <Button variant="text" icon={<UploadOutlined />}>
              Select Image
            </Button>
          </Tooltip>
        )}
      </Upload>
    </ImgCrop>
  );
};
